import type { FC, ReactNode } from 'react'
import type { PersonSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight, MapPin } from 'lucide-react'
import { buttonClass } from '@/components/generals/button'
import { Reveal } from '@/components/generals/reveal'

import PersonAvatar from '@/components/family/person-avatar'
import LifeDates from '@/components/family/life-dates'

interface FocusSectionProps {
    focus: PersonSummary
}

const FocusSection: FC<FocusSectionProps> = ({ focus }): ReactNode => {
    return (
        <section id='heart-of-the-family' className='px-5 py-20 md:px-8 md:py-24'>
            <Reveal className='mx-auto grid max-w-6xl items-center gap-10 rounded-[2rem] border border-line bg-linen p-8 shadow-soft md:grid-cols-[auto_1fr] md:gap-14 md:p-14'>
                <div className='relative mx-auto'>
                    <span aria-hidden='true' className='absolute -inset-4 rounded-full border border-dashed border-gold/60' />
                    <PersonAvatar person={focus} size='xl' />
                </div>

                <div>
                    <p className='flex items-center gap-3 text-xs font-semibold tracking-[0.2em] text-clay uppercase'>
                        <span aria-hidden='true' className='h-px w-8 bg-gold' />
                        At the heart of the tree
                    </p>

                    <h2 className='mt-4 font-heading text-4xl leading-tight font-semibold tracking-tight text-ink md:text-5xl'>
                        <span data-mdlfy-cms={`people:${focus.id}:name`}>
                            {focus.name}
                        </span>
                    </h2>

                    <div className='mt-3 flex flex-wrap items-center gap-x-5 gap-y-2'>
                        <LifeDates person={focus} className='text-base' />

                        {focus.birthplace && (
                            <span className='flex items-center gap-1.5 text-base text-umber'>
                                <MapPin className='size-4 text-forest' aria-hidden='true' />
                                <span data-mdlfy-cms={`people:${focus.id}:birthplace`}>
                                    {focus.birthplace}
                                </span>
                            </span>
                        )}
                    </div>

                    {focus.summary ? (
                        <p data-mdlfy-cms={`people:${focus.id}:summary`} className='mt-6 max-w-2xl text-lg leading-relaxed text-umber'>
                            {focus.summary}
                        </p>
                    ) : (
                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-umber'>
                            This is where the family story begins. Every parent, partner, child and grandchild on this website is connected back to this one name, and every memory shared here adds another page to it.
                        </p>
                    )}

                    <div className='mt-8 flex flex-wrap gap-3'>
                        <Link href={`/people/${focus.slug}`} className={buttonClass({ className: 'group' })}>
                            Read the full story
                            <ArrowRight className='size-4 transition-transform duration-200 group-hover:translate-x-1' aria-hidden='true' />
                        </Link>

                        <Link href='/tree' className={buttonClass({ variant: 'secondary' })}>
                            See the family tree
                        </Link>
                    </div>
                </div>
            </Reveal>
        </section>
    )
}

export default FocusSection
